// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  FiLoader as Loader2, FiRefreshCw as RefreshCw, FiAlertTriangle as AlertTriangle,
  FiCalendar as Calendar, FiTrendingUp as TrendingUp, FiTrendingDown as TrendingDown,
  FiMinus as Minus, FiBarChart2 as BarChart3, FiSun as Lightbulb
} from 'react-icons/fi';
import { getCostForecast, getCostBreakdown, getAnomalies } from '../../../../../services/aiOperationsApi';
import DateRangeFilter from '../../../../../components/dashboard/DateRangeFilter';
import { dateRangeToParams } from '../../../../../components/dashboard/widgetDataFetcher';

const CostsContent = () => {
  const [forecast, setForecast] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastDays, setForecastDays] = useState(30);
  const [breakdownType, setBreakdownType] = useState('agent');
  const [dateRange, setDateRange] = useState({ preset: 'last_30_days', startDate: null, endDate: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = dateRangeToParams(dateRange);

      const [forecastData, breakdownData, anomalyData] = await Promise.all([
        getCostForecast(forecastDays, 30),
        getCostBreakdown({
          days: params.days,
          start_date: params.start_date,
          end_date: params.end_date,
          groupBy: breakdownType
        }),
        getAnomalies(7, 2)
      ]);
      setForecast(forecastData);
      setBreakdown(breakdownData);
      setAnomalies(anomalyData);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [forecastDays, breakdownType, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-5 h-5 text-red-500" />;
    if (trend === 'decreasing') return <TrendingDown className="w-5 h-5 text-green-500" />;
    return <Minus className="w-5 h-5 text-gray-500" />;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    }
  };

  const forecastMax = (forecast?.forecast_daily?.length ?? 0) > 0
    ? Math.max(...forecast.forecast_daily.map(d => d.cost))
    : 0;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cost Analytics</h2>
        <div className="flex items-center gap-3">
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            compact={true}
          />
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {anomalies.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" /> Active Anomalies ({anomalies.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {anomalies.map((anomaly) => (
              <div key={anomaly.id} className={`rounded-lg p-4 border ${getSeverityColor(anomaly.severity)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-medium uppercase">{anomaly.severity}</span>
                    <p className="font-medium mt-1">{anomaly.metric_name.replaceAll('_', ' ')}</p>
                    <p className="text-sm mt-1 opacity-80">{anomaly.description}</p>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-mono">
                      {anomaly.current_value.toFixed(2)} vs {anomaly.expected_value.toFixed(2)}
                    </div>
                    <div className="text-xs opacity-70 mt-1">
                      {anomaly.deviation_std.toFixed(1)} std dev
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {forecast && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-500" /> Cost Forecast
            </h2>
            <select value={forecastDays} onChange={(e) => setForecastDays(Number.parseInt(e.target.value))} className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm">
              <option value="7">Next 7 days</option>
              <option value="14">Next 14 days</option>
              <option value="30">Next 30 days</option>
              <option value="60">Next 60 days</option>
              <option value="90">Next 90 days</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Forecasted Total</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">${forecast.total_forecast.toFixed(2)}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Confidence Range</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">${forecast.confidence_low.toFixed(2)} - ${forecast.confidence_high.toFixed(2)}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Trend</div>
              <div className="flex items-center mt-1">{getTrendIcon(forecast.trend)}<span className="text-lg font-semibold text-gray-900 dark:text-white ml-2 capitalize">{forecast.trend}</span></div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Daily Average</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">${(forecast.total_forecast / forecastDays).toFixed(2)}</div>
            </div>
          </div>

          {(forecast.forecast_daily?.length ?? 0) > 0 && (
            <div className="h-48 relative mb-4">
              <div className="absolute inset-0 flex items-end justify-around">
                {forecast.forecast_daily.map((day, index) => (
                  <div
                    key={day.date}
                    className="flex-1 mx-0.5 bg-blue-200 dark:bg-blue-700 rounded-t hover:bg-blue-300 dark:hover:bg-blue-600 transition-colors group relative cursor-pointer"
                    style={{
                      height: forecastMax > 0 ? `${(day.cost / forecastMax) * 100}%` : '0%',
                      minHeight: '4px'
                    }}
                    title={`${day.date}: $${day.cost.toFixed(2)}`}
                  >
                    <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                      {day.date}: ${day.cost.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(forecast.recommendations?.length ?? 0) > 0 && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center mb-2"><Lightbulb className="w-4 h-4 mr-2" /> Recommendations</h3>
              <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                {forecast.recommendations.map((rec, i) => <li key={`rec-${rec.slice(0, 40)}-${i}`} className="flex items-start"><span className="mr-2">•</span>{rec}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {breakdown && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-purple-500" /> Cost Breakdown
            </h2>
            <div className="flex items-center space-x-4">
              <select value={breakdownType} onChange={(e) => setBreakdownType(e.target.value)} className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm">
                <option value="agent">By Agent</option>
                <option value="prompt">By Prompt</option>
                <option value="model">By Model</option>
                <option value="day">By Day</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{breakdownType === 'day' ? 'Date' : 'Name'}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Cost</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Runs</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg/Run</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {breakdown.breakdown.slice(0, 15).map((item, index) => {
                  const totalCost = breakdown.breakdown.reduce((sum, b) => sum + b.total_cost, 0);
                  const share = totalCost > 0 ? (item.total_cost / totalCost) * 100 : 0;
                  const avgPerRun = item.run_count > 0 ? item.total_cost / item.run_count : 0;
                  return (
                    <tr key={item.name || item.date || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">{item.name || item.date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-mono">${item.total_cost.toFixed(4)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">{item.run_count.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right font-mono">${avgPerRun.toFixed(4)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                            <div
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${Math.min(share, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {share.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {breakdown.breakdown.length > 15 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
              Showing top 15 of {breakdown.breakdown.length} items
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CostsContent;
